import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { hasModuleAccess } from '../utils/permissions';
import {
  FaMapMarkerAlt,
  FaBus,
  FaUserGraduate,
  FaUserFriends,
  FaRoute,
  FaChartLine,
  FaCreditCard,
  FaUsers,
  FaChevronLeft,
  FaRobot,
  FaNetworkWired,
  FaClipboardList,
  FaCog,
  FaMapPin,
  FaTachometerAlt,
  FaUserTie,
  FaEnvelope
} from 'react-icons/fa';
import styles from '../styles/Sidebar.module.css';

const menuItems = [
  { icon: <FaTachometerAlt />, label: 'Dashboard', path: '/dashboard' },
  { icon: <FaBus />, label: 'Fleet', path: '/fleet' },
  { icon: <FaMapMarkerAlt />, label: 'Track', path: '/track' },
  { icon: <FaUserTie />, label: 'Personnel', path: '/personnel' },
  { icon: <FaUserGraduate />, label: 'Students', path: '/students' },
  { icon: <FaUserFriends />, label: 'Parents', path: '/parents' },
  { icon: <FaClipboardList />, label: 'Roster', path: '/roster' },
  { 
    icon: <FaRoute />, 
    label: 'Routes', 
    path: '/routes',
    submenu: [
      { icon: <FaCog />, label: 'Engine', path: '/routes/engine' },
      { icon: <FaMapPin />, label: 'Optimized', path: '/routes/optimized' }
    ]
  },
  { icon: <FaChartLine />, label: 'Analytics', path: '/analytics' },
  { icon: <FaRobot />, label: 'AI Detect', path: '/ai-detection' },
  { icon: <FaNetworkWired />, label: 'Devices', path: '/devices' },
  { icon: <FaEnvelope />, label: 'Messages', path: '/messages' },
  { icon: <FaCreditCard />, label: 'Billing', path: '/billing' },
  { icon: <FaUsers />, label: 'Users', path: '/users' },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [filteredMenuItems, setFilteredMenuItems] = useState(menuItems); // Start with all items, filter after role is loaded
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const role = userDoc.data().role;
          
          // Filter menu items based on role
          const filtered = menuItems.map(item => {
            // Check if main menu item is accessible
            if (!hasModuleAccess(role, item.path)) {
              return null;
            }
            
            // If item has submenu, filter submenu items (create a new object to avoid mutation)
            if (item.submenu) {
              const filteredSubmenu = item.submenu.filter(subItem => 
                hasModuleAccess(role, subItem.path)
              );
              // Only show parent if at least one submenu item is accessible
              if (filteredSubmenu.length === 0) {
                return null;
              }
              // Return a new object with filtered submenu
              return {
                ...item,
                submenu: filteredSubmenu
              };
            }
            
            return item;
          }).filter(item => item !== null); // Remove null items
          
          setFilteredMenuItems(filtered);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, []);

  const handleMenuClick = (item) => {
    if (item.submenu) {
      setExpandedMenu(expandedMenu === item.path ? null : item.path);
    } else {
      navigate(item.path);
    }
  };

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>  
      <div style={{ height: '2rem' }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <nav className={styles.menu} style={{ flex: 1 }}>
          {filteredMenuItems.map((item, idx) => (
            <div key={idx}>
              <div
                onClick={() => handleMenuClick(item)}
                className={`${styles.menuItem} ${
                  location.pathname === item.path ? styles.menuItemActive : ''
                }`}
              >
                <span className={styles.icon}>{item.icon}</span>
                {!collapsed && <span className={styles.label}>{item.label}</span>}
              </div>
              {!collapsed && item.submenu && expandedMenu === item.path && (
                <div className={styles.submenu}>
                  {item.submenu.map((subItem, subIdx) => (
                    <div
                      key={subIdx}
                      onClick={() => navigate(subItem.path)}
                      className={`${styles.menuItem} ${styles.submenuItem} ${
                        location.pathname === subItem.path ? styles.menuItemActive : ''
                      }`}
                    >
                      <span className={styles.icon}>{subItem.icon}</span>
                      <span className={styles.label}>{subItem.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <button
          className={styles.collapseBtn}
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{ marginTop: '2rem', marginBottom: '2rem' }}
        >
          <FaChevronLeft className={collapsed ? styles.rotateIcon : ''} />
          {!collapsed && <span className={styles.collapseText}>Collapse</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar; 